import { createContext, useCallback, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LOCAL_FEATURE_FLAGS, LocalFeatureFlags } from './feature-flags-helper';
import { getFeatureFlagOverridesFromEnv, getURLParamsFeatureFlagOverrides } from './feature-flag-overrides';
import {
  ClientFeatureFlags,
  FEATURE_FLAG_QUERY_VARIABLES,
  DEFAULT_CLIENT_FEATURE_FLAGS,
  mapFeatureFlagResponse,
} from './feature-flags-utils';
import { GetFeatureFlagsV2, GetFeatureFlagsV2Variables, GetFeatureFlagsV2Context } from '@/generated/graphql';
import { logError } from '@/errors';
import { useIsAuthenticated, useIsInternalUser } from '@/authentication';
import { gql, useQuery } from '@/graphql';
import { useLocalStorage } from '@/browser-storage';

/**
 * A client-side representation of all available feature flags (both from the GQL API and local).
 */
export type FeatureFlags = ClientFeatureFlags & LocalFeatureFlags;

/**
 * Default values for all feature flags, both from the GQL API and local feature flags.
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  ...DEFAULT_CLIENT_FEATURE_FLAGS,
  ...LOCAL_FEATURE_FLAGS,
};

const GET_FEATURE_FLAGS = gql`
  query GetFeatureFlagsV2($flags: [FeatureFlagRequest!]!) {
    featureFlagsV2(flags: $flags) {
      key
      value {
        ... on BoolValue {
          boolValue: value
        }
        ... on StringValue {
          stringValue: value
        }
        ... on IntValue {
          intValue: value
        }
      }
    }
  }
`;

/**
 * @internal This should not be used directly, use the `FeatureFlagContextProvider` instead
 */
export const FeatureFlagContext = createContext<{
  isLoading: boolean;
  featureFlags: FeatureFlags;
  setFeatureFlags: (flags: FeatureFlags) => void;
  originalFeatureFlags: FeatureFlags;
  reset: () => void;
}>({
  isLoading: false,
  originalFeatureFlags: DEFAULT_FEATURE_FLAGS,
  featureFlags: DEFAULT_FEATURE_FLAGS,
  setFeatureFlags: () => {
    // noop
  },
  reset: () => {
    // noop
  },
});

/**
 * Context provider that loads feature flag state from the GQL API, combines it with any local flag state, and makes
 * the flags available to the rest of the application.
 */
export function FeatureFlagContextProvider(props: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const [searchParams] = useSearchParams();

  const [{ data, error, fetching }] = useQuery<GetFeatureFlagsV2, GetFeatureFlagsV2Variables>({
    query: GET_FEATURE_FLAGS,
    context: GetFeatureFlagsV2Context,
    variables: FEATURE_FLAG_QUERY_VARIABLES,
    pause: !isAuthenticated,
  });

  // Since we "handle" this error instead of throwing it, make sure it gets reported
  useEffect(() => {
    if (error === undefined || error === null) return;
    logError(error);
  }, [error]);

  const flags = { ...mapFeatureFlagResponse(data), ...LOCAL_FEATURE_FLAGS };

  const isChronoAdmin = useIsInternalUser();
  const envConfigLocalOverrides = getFeatureFlagOverridesFromEnv();

  const [localStorageFeatureFlags, setLocalStorageFeatureFlags] = useLocalStorage('featureFlagsOverrides', {});
  // This hook is responsible for checking if there are any URL params that override the feature flags
  useEffect(() => {
    const paramOverrides = getURLParamsFeatureFlagOverrides(searchParams.toString());
    // If there are URL param overrides, we need to update the local storage overrides with them
    if (Object.keys(paramOverrides).length > 0) {
      // Confirm with the user if they have existing local storage overrides
      if (Object.keys(localStorageFeatureFlags).length > 0) {
        const confirmed = window.confirm(
          'You have opened a link with URL feature flag overrides but you have existing overrides set. Do you want to apply the overrides from the URL? Any existing overrides will be overwritten.'
        );
        if (!confirmed) {
          return;
        }
      }
      setLocalStorageFeatureFlags(paramOverrides);
    }
    // Only do this once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the local storage overrides to original state
  const reset = useCallback(() => {
    setLocalStorageFeatureFlags({}); // Clear the local storage overrides
  }, [setLocalStorageFeatureFlags]);

  // Only merge the flags if the user is a Chrono Admin
  const mergedFlags = isChronoAdmin
    ? {
        ...flags,
        ...envConfigLocalOverrides,
        ...localStorageFeatureFlags,
      }
    : flags;

  return (
    <FeatureFlagContext.Provider
      value={{
        isLoading: fetching,
        originalFeatureFlags: flags,
        featureFlags: mergedFlags,
        setFeatureFlags: setLocalStorageFeatureFlags,
        reset,
      }}
    >
      {props.children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlagsState() {
  const { featureFlags, setFeatureFlags, originalFeatureFlags, reset } = useContext(FeatureFlagContext);
  return {
    originalFeatureFlags,
    featureFlags,
    setFeatureFlags,
    reset,
  };
}

export function useFeatureFlags() {
  const { featureFlags } = useFeatureFlagsState();
  return featureFlags;
}

/**
 * Returns the current product name based on the rebrand feature flag.
 * Also exports a `getProductName` helper for non-hook contexts.
 */
export function getProductName(panwRebrandStubYear: boolean | undefined): string {
  return panwRebrandStubYear ? 'XCOR' : 'Chronosphere';
}

export function useProductName(): string {
  const { panwRebrandStubYear } = useFeatureFlags();
  return getProductName(panwRebrandStubYear);
}

export function useIsFeatureFlagsLoading() {
  const featureFlagsContext = useContext(FeatureFlagContext);
  return featureFlagsContext.isLoading;
}

export type FeatureFlagGateProps = {
  /**
   * A function that takes the current feature flags and returns a boolean indicating whether the child
   * component should be rendered.
   */
  condition: (args: { featureFlags: FeatureFlags }) => boolean;
  /**
   * The component to render if the condition is not met.
   */
  fallback?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * A component that conditionally renders its children based on the provided condition and the current feature flags.
 */
export function FeatureFlagGate(props: FeatureFlagGateProps) {
  const { condition, fallback, children } = props;

  const { featureFlags } = useFeatureFlagsState();
  const isAllowed = condition({ featureFlags });

  if (!isAllowed) return fallback;

  return children;
}
